import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { createProject } from '../lib/api';
import { friendlyError } from '../lib/format';
import { Badge, Button, Empty, ErrorText, Modal, PageHeader, TextField } from '../components/ui';

export function Projects() {
  const { projects, isManager, reload } = useAuth();
  const [adding, setAdding] = useState(false);

  return (
    <div>
      <PageHeader title="Your projects" subtitle="Everything your team needs to answer buyer questions, in one place."
        action={isManager && <Button variant="primary" onClick={() => setAdding(true)}>+ Add project</Button>} />
      {projects.length === 0 ? (
        <Empty title="No projects yet">{isManager ? 'Add the projects your firm is selling.' : 'Your manager adds the projects your firm sells.'}</Empty>
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {projects.map((p) => (
            <li key={p.id}><Link to={`/projects/${p.id}`} className="group flex h-full flex-col rounded-[20px] border border-line bg-white p-5 shadow-[0_8px_26px_rgba(31,57,89,.035)] transition-colors hover:border-[#b7c6d6] hover:shadow-md">
              <div className="mb-5 flex items-center justify-between gap-3"><div className="grid h-11 w-11 place-items-center rounded-xl bg-accentSoft text-lg font-extrabold text-accent">{p.name.trim().slice(0, 1).toUpperCase()}</div>
                <Badge tone={p.status === 'approved' ? 'ok' : 'warn'}>{p.status === 'approved' ? 'Facts approved' : 'Needs setup'}</Badge></div>
              <p className="text-lg font-extrabold tracking-tight text-ink">{p.name}</p>
              <p className="mt-1 text-sm text-muted">{[p.builder, p.micro_market, p.city].filter(Boolean).join(' · ') || 'No details yet'}</p>
              <p className="mt-3 text-xs text-muted">{p.rera_number ? `RERA ${p.rera_number}` : 'RERA number not added'}</p>
              <div className="mt-auto flex items-center justify-between border-t border-line pt-4 text-sm font-bold text-accent" style={{ marginTop: '24px' }}><span>Open workspace</span><span aria-hidden="true" className="transition-transform group-hover:translate-x-1">→</span></div>
            </Link></li>
          ))}
        </ul>
      )}
      {adding && <AddProjectModal onClose={() => setAdding(false)} onSaved={async () => { setAdding(false); await reload(); }} />}
    </div>
  );
}

function AddProjectModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState('');
  const [builder, setBuilder] = useState('');
  const [microMarket, setMicroMarket] = useState('');
  const [city, setCity] = useState('Gurugram');
  const [rera, setRera] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError('');
    try { await createProject({ name, builder, microMarket, city, rera }); onSaved(); } catch (err) { setError(friendlyError(err)); setBusy(false); }
  }

  return (
    <Modal open onClose={onClose} title="Add a project">
      <form onSubmit={submit} className="flex flex-col gap-4">
        <TextField label="Project name" required value={name} onChange={(e) => setName(e.target.value)} />
        <TextField label="Builder" value={builder} onChange={(e) => setBuilder(e.target.value)} />
        <TextField label="Area" value={microMarket} onChange={(e) => setMicroMarket(e.target.value)} placeholder="Dwarka Expressway" />
        <TextField label="City" value={city} onChange={(e) => setCity(e.target.value)} />
        <TextField label="Project RERA number" value={rera} onChange={(e) => setRera(e.target.value)} />
        <ErrorText>{error}</ErrorText>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={busy}>{busy ? 'Saving…' : 'Add project'}</Button>
        </div>
      </form>
    </Modal>
  );
}
